import { serviceColor as colorForService } from '@/lib/map/layers/surveillance';
import { useSurvStore, selectSurvServices } from '@/lib/store/surv';
import { useShallow } from 'zustand/shallow';
import { Badge } from '@/components/ui/badge';
import { Section } from '@/components/Section';
import { cn } from '@/lib/utils';
import type { SurveillanceService, SensorStation } from '@/lib/mqtt/schemas/surveillance';
import type { BadgeClass } from '@/lib/store/drones';
import { FusedServicePanel } from './FusedServicePanel';

export function SurvList() {
  const services = useSurvStore(useShallow(selectSurvServices));
  const setNetwork = useSurvStore((s) => s.setNetwork);
  const setMode = useSurvStore((s) => s.setMode);
  const mode = useSurvStore((s) => s.mode);
  const selectedNetwork = useSurvStore((s) => s.selectedNetwork);

  // Mockup `selectSurvNet` opens the detail panel for a service. In v2 that
  // means flipping to Diagnostic + setting `selectedNetwork`. The SurvContext
  // root then swaps to SurvDetail.
  const openService = (serviceId: string) => {
    setMode('diagnostic');
    setNetwork(serviceId);
  };

  const active = services.filter((s) => s.service_state === 'Active').length;
  const degraded = services.filter((s) => s.service_state === 'Degraded').length;
  const connecting = services.filter((s) => s.service_state === 'Connecting').length;
  const offline = services.filter((s) => s.service_state === 'Offline').length;
  // Sensor totals must exclude Live_Fused: its counts are the sum of its
  // inputs' counts, so including it would count every fused receiver twice.
  // The service-level counts above DO include it — it is a service in its own
  // right, it just owns no additional hardware.
  const physical = services.filter((s) => s.service_id !== 'live_fused');
  const totalOnline = physical.reduce((acc, s) => acc + s.online_sensors, 0);
  const totalOffline = physical.reduce((acc, s) => acc + (s.total_sensors - s.online_sensors), 0);

  // Build the header subtitle: list every non-Active state separately
  // so the operator sees the full breakdown ("5 active · 1 offline"
  // instead of a misleading "all active"). When every service is
  // Active, collapse to "all active".
  const subtitleParts: string[] = [`${services.length} services`];
  if (degraded > 0) subtitleParts.push(`${degraded} degraded`);
  if (offline > 0) subtitleParts.push(`${offline} offline`);
  if (connecting > 0) subtitleParts.push(`${connecting} connecting`);
  if (degraded === 0 && offline === 0 && connecting === 0) {
    subtitleParts.push('all active');
  }
  const subtitle = subtitleParts.join(' · ');

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Mockup LP header (L3306-L3312): title + counts subtitle only. The
          Fused/Diagnostic toggle lives in the topbar pill row — duplicating
          it inside the panel adds noise without value. */}
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <div className="text-[0.846em] font-bold">Active Surveillance</div>
        <div className="text-[0.692em] text-(--color-text3) font-mono">{subtitle}</div>
      </header>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        <div className="grid grid-cols-2 gap-1.5">
          <StatTile label="Active Services" value={active} tone="ok" />
          <StatTile label="Degraded Services" value={degraded} tone={degraded > 0 ? 'warn' : 'ok'} />
          <StatTile label="Sensors Online" value={totalOnline} tone="ok" />
          <StatTile label="Sensors Offline" value={totalOffline} tone={totalOffline > 0 ? 'crit' : 'ok'} />
        </div>
        {mode === 'fused' ? (
          <>
            <Section title="Services">
              <div className="space-y-1.5">
                {services.length === 0 && <div className="text-[0.769em] text-(--color-text3) text-center py-4">Waiting for surveillance data…</div>}
                {services.map((s) => (
                  <ServiceCard key={s.service_id} svc={s} onClick={() => openService(s.service_id)} />
                ))}
              </div>
            </Section>
            <FusedServicePanel />
          </>
        ) : (
          <Section title={selectedNetwork ? `Sensors — ${selectedNetwork}` : 'Sensors'}>
            <p className="text-[0.692em] text-(--color-text3) -mt-1 mb-1.5 leading-snug">
              Per-sensor health for {selectedNetwork ? 'the selected service' : 'all services'}. Pick a service above to filter.
            </p>
            <div className="space-y-1.5">
              {services
                .filter((svc) => !selectedNetwork || svc.service_id === selectedNetwork)
                .flatMap((svc) => svc.stations.map((st) => <SensorCard key={`${svc.service_id}|${st.sensor_id}`} svc={svc} st={st} />))}
              {services.flatMap((s) => s.stations).length === 0 && (
                <div className="text-[0.769em] text-(--color-text3) text-center py-4">No sensors reporting.</div>
              )}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function SensorCard({ svc, st }: { svc: SurveillanceService; st: SensorStation }) {
  const tone: BadgeClass = st.state === 'live' ? 'ok' : st.state === 'degraded' ? 'warn' : 'crit';
  return (
    <article
      className={cn(
        'rounded-md border border-(--color-border) bg-(--color-surface) p-2 border-l-2',
        tone === 'ok' && 'border-l-(--color-green)',
        tone === 'warn' && 'border-l-(--color-amber)',
        tone === 'crit' && 'border-l-(--color-red)',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[0.769em] font-bold truncate">{st.label}</span>
        <Badge variant={tone}>{st.state}</Badge>
      </div>
      <div className="text-[0.615em] font-mono text-(--color-text3) mt-0.5 truncate">{svc.label}</div>
      <div className="flex items-center gap-2 mt-1 text-[0.615em] font-mono text-(--color-text3)">
        <span>{st.latency_ms != null ? `${st.latency_ms}ms` : '—'}</span>
        <span>·</span>
        <span>{st.rssi != null ? `${st.rssi} dBm` : 'rssi —'}</span>
        <span>·</span>
        <span>{st.target_count} tgt</span>
      </div>
    </article>
  );
}

function StatTile({ label, value, tone }: { label: string; value: number; tone: BadgeClass }) {
  return (
    <div className="rounded-md border border-(--color-border) bg-(--color-surface) p-2.5 border-t-2 border-t-(--color-border)">
      <div
        className={cn(
          'text-xl font-bold font-mono',
          tone === 'ok' && 'text-(--color-green)',
          tone === 'warn' && 'text-(--color-amber)',
          tone === 'crit' && 'text-(--color-red)',
        )}
      >
        {value}
      </div>
      <div className="text-[0.615em] text-(--color-text3) font-bold uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function ServiceCard({ svc, onClick }: { svc: SurveillanceService; onClick: () => void }) {
  // Connecting → 'info' (neutral blue, signals "in progress" without
  // raising an alert tone the way crit/red would for an actual Offline).
  const tone: BadgeClass =
    svc.service_state === 'Active'
      ? 'ok'
      : svc.service_state === 'Degraded'
        ? 'warn'
        : svc.service_state === 'Connecting'
          ? 'info'
          : 'crit';
  // Display "Connecting…" with the ellipsis the user expects; other
  // states render the enum value verbatim.
  const stateLabel = svc.service_state === 'Connecting' ? 'Connecting…' : svc.service_state;
  const offline = svc.total_sensors - svc.online_sensors;
  // Top-border colour mirrors the service's pin colour on the map (mockup
  // `SURV_SERVICE_COLOURS`). State (Active/Degraded/Offline) is conveyed
  // by the right-side status Badge instead — keeping the border tied to
  // identity makes the panel scannable when several services are degraded
  // at once.
  const serviceColor = colorForService(svc.service_id);
  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onClick}
      style={{ borderTopColor: serviceColor }}
      className="rounded-md border border-(--color-border) bg-(--color-surface) p-2.5 border-t-2 cursor-pointer hover:border-(--color-green) hover:bg-(--color-green-bg) transition-colors"
    >
      {/* Mockup `surv-net-item` (L9919-L9927): top row is label (mono) + type
          chip grouped left, status badge right. Bottom row is the sensor
          breakdown "{total} sensors · {N} online[· {M} offline] · {trust} Trust". */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[0.923em] font-bold font-mono truncate">{svc.label}</span>
          <Badge variant={svc.type === 'Cooperative' ? 'info' : 'muted'}>{svc.type}</Badge>
        </div>
        <Badge variant={tone}>{stateLabel}</Badge>
      </div>
      <div className="flex items-center flex-wrap gap-x-1.5 mt-1 text-[0.692em] font-mono text-(--color-text3)">
        <span>{svc.total_sensors} sensors</span>
        <span>·</span>
        <span className="text-(--color-green) font-semibold">{svc.online_sensors} online</span>
        {offline > 0 && (
          <>
            <span>·</span>
            <span className="text-(--color-red) font-semibold">{offline} offline</span>
          </>
        )}
        <span>·</span>
        <span>{svc.trust} Trust</span>
      </div>
    </article>
  );
}
